import { useMemo, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { PageHeader, StatusPill } from "@/components/admin/PageHeader";
import {
  cancelCampaignFn,
  campaignReportFn,
  deleteCampaignFn,
  duplicateCampaignFn,
  listCampaignsFn,
  sendCampaignFn,
} from "@/lib/campaigns/campaigns.functions";
import {
  CAMPAIGN_STATUS_LABEL,
  CAMPAIGN_TYPE_LABEL,
  isEditable,
  type CampaignStatus,
  type CampaignType,
} from "@/lib/campaigns/types";

export const Route = createFileRoute("/admin/campaigns/")({ component: CampaignsPage });

const CARD = "border border-black/10 bg-white p-4";
const LABEL = "text-[10px] font-semibold uppercase tracking-[0.2em] text-black/45";
const BTN = "border border-black/20 px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.16em]";

const TONE: Record<CampaignStatus, "neutral" | "success" | "warn" | "danger" | "info"> = {
  draft: "neutral",
  scheduled: "info",
  sending: "warn",
  pending_delivery: "warn",
  completed: "success",
  failed: "danger",
  cancelled: "neutral",
};

function fmt(v: string | null) {
  if (!v) return "—";
  return new Date(v).toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function CampaignsPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const query = useQuery({ queryKey: ["campaigns", "list"], queryFn: () => listCampaignsFn() });
  const report = useQuery({ queryKey: ["campaigns", "report"], queryFn: () => campaignReportFn() });
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<"all" | CampaignStatus>("all");
  const [type, setType] = useState<"all" | CampaignType>("all");

  const invalidate = () => qc.invalidateQueries({ queryKey: ["campaigns"] });

  const duplicate = useMutation({
    mutationFn: (id: string) => duplicateCampaignFn({ data: { id } }),
    onSuccess: (r) => {
      void invalidate();
      toast.success("Campaign duplicated");
      void navigate({ to: "/admin/campaigns/$id", params: { id: r.id } });
    },
    onError: (e: Error) => toast.error(e.message || "Could not duplicate"),
  });

  const remove = useMutation({
    mutationFn: (id: string) => deleteCampaignFn({ data: { id } }),
    onSuccess: () => {
      void invalidate();
      toast.success("Campaign deleted");
    },
    onError: (e: Error) => toast.error(e.message || "Could not delete"),
  });

  const send = useMutation({
    mutationFn: (id: string) => sendCampaignFn({ data: { id } }),
    onSuccess: (r) => {
      void invalidate();
      toast.success(`Sent ${r.sent} · failed ${r.failed} · remaining ${r.remaining}`);
    },
    onError: (e: Error) => toast.error(e.message || "Send failed"),
  });

  const cancel = useMutation({
    mutationFn: (id: string) => cancelCampaignFn({ data: { id } }),
    onSuccess: () => {
      void invalidate();
      toast.success("Campaign cancelled");
    },
    onError: (e: Error) => toast.error(e.message || "Could not cancel"),
  });

  const all = query.data?.campaigns ?? [];
  const provider = query.data?.provider;

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return all.filter((c) => {
      if (status !== "all" && c.status !== status) return false;
      if (type !== "all" && c.type !== type) return false;
      if (!q) return true;
      return `${c.name} ${c.subject} ${c.createdByEmail}`.toLowerCase().includes(q);
    });
  }, [all, search, status, type]);

  const totals = useMemo(
    () => ({
      total: all.length,
      scheduled: all.filter((c) => c.status === "scheduled").length,
      completed: all.filter((c) => c.status === "completed").length,
      failed: all.filter((c) => c.status === "failed").length,
    }),
    [all],
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title="Email Campaigns"
        subtitle="Create, schedule and dispatch campaigns to your subscriber base."
        action={
          <Link to="/admin/campaigns/$id" params={{ id: "new" }} className={`${BTN} bg-black text-white`}>
            New campaign
          </Link>
        }
      />

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        {[
          ["Campaigns", totals.total],
          ["Scheduled", totals.scheduled],
          ["Completed", totals.completed],
          ["Failed", totals.failed],
          ["Active subscribers", query.data?.activeSubscribers ?? 0],
        ].map(([label, value]) => (
          <div key={String(label)} className={CARD}>
            <p className={LABEL}>{label}</p>
            <p className="mt-2 text-2xl font-semibold text-black">{value}</p>
          </div>
        ))}
      </div>

      <div className={`${CARD} flex flex-wrap items-center gap-3`}>
        <span className={LABEL}>Email provider</span>
        <StatusPill tone={provider?.configured ? "success" : "warn"}>
          {provider?.activeLabel ?? "Not configured"}
        </StatusPill>
        <span className="text-xs text-black/50">
          {provider?.available.map((p) => `${p.label}${p.configured ? " ✓" : ""}`).join(" · ")}
        </span>
      </div>

      <div className={`${CARD} space-y-3`}>
        <p className={LABEL}>Delivery performance</p>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {[
            ["Emails sent", report.data?.totals.sent ?? 0],
            ["Open rate", `${report.data?.rates.openRate ?? 0}%`],
            ["Click-through rate", `${report.data?.rates.ctr ?? 0}%`],
            ["Bounce rate", `${report.data?.rates.bounceRate ?? 0}%`],
            ["Opened", report.data?.totals.opened ?? 0],
            ["Clicked", report.data?.totals.clicked ?? 0],
            ["Bounced", report.data?.totals.bounced ?? 0],
            ["Unsubscribed", report.data?.totals.unsubscribed ?? 0],
          ].map(([label, value]) => (
            <div key={String(label)} className="border border-black/10 p-3">
              <p className={LABEL}>{label}</p>
              <p className="mt-1 text-xl text-black">{String(value)}</p>
            </div>
          ))}
        </div>
        {(report.data?.topCampaigns.length ?? 0) > 0 && (
          <ul className="space-y-1 text-[13px] text-black/60">
            {(report.data?.topCampaigns ?? []).map((c) => (
              <li key={c.id} className="flex flex-wrap justify-between gap-2 border-b border-black/5 pb-1">
                <span className="text-black">{c.name}</span>
                <span>{c.sent} sent · {c.openRate}% opened</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className={`${CARD} flex flex-wrap gap-3`}>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search campaigns"
          className="min-w-[200px] flex-1 border border-black/15 px-3 py-2 text-sm focus:border-black/50 focus:outline-none"
        />
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value as typeof status)}
          className="border border-black/15 px-3 py-2 text-sm"
        >
          <option value="all">All statuses</option>
          {Object.entries(CAMPAIGN_STATUS_LABEL).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
        <select
          value={type}
          onChange={(e) => setType(e.target.value as typeof type)}
          className="border border-black/15 px-3 py-2 text-sm"
        >
          <option value="all">All types</option>
          {Object.entries(CAMPAIGN_TYPE_LABEL).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
      </div>

      <div className="overflow-x-auto border border-black/10 bg-white">
        <table className="w-full min-w-[1100px] text-sm">
          <thead>
            <tr className="border-b border-black/10 text-left">
              {["Campaign","Subject","Type","Audience","Sent","Delivered","Failed","Scheduled","Created by","Created","Status","Actions"].map((h) => (
                <th key={h} className={`px-3 py-2 ${LABEL}`}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {query.isLoading && (
              <tr><td colSpan={12} className="px-3 py-6 text-center text-black/50">Loading campaigns…</td></tr>
            )}
            {!query.isLoading && rows.length === 0 && (
              <tr><td colSpan={12} className="px-3 py-6 text-center text-black/50">No campaigns yet.</td></tr>
            )}
            {rows.map((c) => (
              <tr key={c.id} className="border-b border-black/5 align-top">
                <td className="px-3 py-3">
                  <Link to="/admin/campaigns/$id" params={{ id: c.id }} className="font-medium text-black underline-offset-4 hover:underline">
                    {c.name}
                  </Link>
                </td>
                <td className="px-3 py-3 text-black/70">{c.subject}</td>
                <td className="px-3 py-3 text-black/70">{CAMPAIGN_TYPE_LABEL[c.type]}</td>
                <td className="px-3 py-3 text-black/70">{c.recipientsTotal}</td>
                <td className="px-3 py-3 text-black/70">{c.sentCount + c.failedCount}</td>
                <td className="px-3 py-3 text-black/70">{c.sentCount}</td>
                <td className="px-3 py-3 text-black/70">{c.failedCount}</td>
                <td className="px-3 py-3 text-black/70">{fmt(c.scheduledAt)}</td>
                <td className="px-3 py-3 text-black/70">{c.createdByEmail || "—"}</td>
                <td className="px-3 py-3 text-black/70">{fmt(c.createdAt)}</td>
                <td className="px-3 py-3"><StatusPill tone={TONE[c.status]}>{CAMPAIGN_STATUS_LABEL[c.status]}</StatusPill></td>
                <td className="px-3 py-3">
                  <div className="flex flex-wrap gap-2">
                    <Link to="/admin/campaigns/$id" params={{ id: c.id }} className={BTN}>
                      {isEditable(c.status) ? "Edit" : "View"}
                    </Link>
                    <button type="button" className={BTN} onClick={() => duplicate.mutate(c.id)}>Duplicate</button>
                    {(c.status === "sending" || c.status === "failed") && (
                      <button type="button" className={BTN} onClick={() => send.mutate(c.id)} disabled={send.isPending}>
                        Resume
                      </button>
                    )}
                    {c.status === "scheduled" && (
                      <button type="button" className={BTN} onClick={() => cancel.mutate(c.id)}>Cancel</button>
                    )}
                    {c.status !== "sending" && (
                      <button
                        type="button"
                        className={`${BTN} text-red-700`}
                        onClick={() => {
                          if (window.confirm(`Delete "${c.name}"?`)) remove.mutate(c.id);
                        }}
                      >
                        Delete
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
